(() => {
  const cache = window.TiiProductCache || (window.TiiProductCache = new Map());
  const outlinkCache = window.TiiOutlinkCache || (window.TiiOutlinkCache = new Map());
  const queue = [];
  let activeRequests = 0;
  const runQueue = () => {
    while (activeRequests < 4 && queue.length) {
      const task = queue.shift();
      activeRequests += 1;
      task().finally(() => { activeRequests -= 1; runQueue(); });
    }
  };
  const queuedFetch = (url, attempt = 0) => new Promise((resolve, reject) => {
    queue.push(async () => {
      try {
        const response = await fetch(url);
        if (response.status === 429 && attempt < 3) {
          await new Promise(done => setTimeout(done, 450 * (attempt + 1)));
          resolve(queuedFetch(url, attempt + 1));
          return;
        }
        if (!response.ok) throw new Error(`${response.status}`);
        resolve(await response.json());
      } catch (error) { reject(error); }
    });
    runQueue();
  });
  const getProduct = (handle) => {
    if (!cache.has(handle)) cache.set(handle, queuedFetch(`/products/${encodeURIComponent(handle)}.js`));
    return cache.get(handle);
  };
  const getOutlink = (handle) => {
    if (!outlinkCache.has(handle)) {
      outlinkCache.set(handle, queuedFetch(`/products/${encodeURIComponent(handle)}?view=tii-card`));
    }
    return outlinkCache.get(handle);
  };
  const getCardData = async (handle) => {
    const [product, outlink] = await Promise.all([getProduct(handle), getOutlink(handle)]);
    return { product, outlink };
  };
  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const card = ({ product, outlink }) => {
    const image = product.featured_image || product.images?.[0] || '';
    const item = document.createElement('article');
    item.className = 'tii-prefall__card';
    item.dataset.productHandle = product.handle;
    const alt = [product.vendor, product.title].filter(Boolean).join(' — ');
    const label = outlink?.label || `Shop at ${product.vendor || 'retailer'}`;
    item.innerHTML = `<a class="tii-prefall__card-main"><span class="tii-prefall__card-media"><img src="${escapeHtml(image)}" alt="${escapeHtml(alt)}" loading="lazy"></span><span class="tii-prefall__card-vendor">${escapeHtml(product.vendor)}</span><span class="tii-prefall__card-title">${escapeHtml(product.title)}</span></a>`;

    const mainLink = item.querySelector('.tii-prefall__card-main');
    mainLink.href = product.url || `/products/${product.handle}`;

    if (outlink?.external && outlink.url) {
      const shopLink = document.createElement('a');
      shopLink.className = 'tii-prefall__card-outlink';
      shopLink.href = outlink.url;
      shopLink.target = '_blank';
      shopLink.rel = 'nofollow sponsored noopener';
      shopLink.dataset.outlinkProduct = product.handle;
      shopLink.textContent = label;
      item.append(shopLink);
    }
    return item;
  };
  const handles = (value) => [...new Set((value || '').split(',').map(v => v.trim()).filter(Boolean))];

  class TiiPrefallStory extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.rails = [...this.querySelectorAll('[data-product-rail]')];
      this.rails.forEach(rail => rail.innerHTML = '<span class="tii-prefall__skeleton"></span><span class="tii-prefall__skeleton"></span><span class="tii-prefall__skeleton"></span>');
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const rail = entry.target;
        observer.unobserve(rail);
        this.loadRail(rail);
      }), { rootMargin: '500px 0px' });
      this.rails.forEach(rail => observer.observe(rail));
      this.setupVideos();
      this.setupCollection();
    }
    setupVideos() {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const videos = [...this.querySelectorAll('[data-story-video]')];
      const loadVideo = (video) => {
        const source = video.querySelector('source[data-src]');
        if (!source) return;
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        video.load();
      };

      // Fetch the asset shortly before it is needed, while deliberately keeping
      // playback separate so the title animation cannot run off-screen.
      const preloadObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const video = entry.target;
        preloadObserver.unobserve(video);
        loadVideo(video);
      }), { rootMargin: '500px 0px', threshold: 0 });

      const playbackObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        const video = entry.target;
        const requiredRatio = video === videos[0] ? 0.62 : 0.35;
        if (entry.isIntersecting && entry.intersectionRatio >= requiredRatio) {
          loadVideo(video);
          if (!reduced && !video.ended) video.play().catch(() => {});
          return;
        }
        video.pause();
      }), { rootMargin: '0px', threshold: [0, .35, .62] });

      videos.forEach(video => {
        video.pause();
        preloadObserver.observe(video);
        playbackObserver.observe(video);
      });
    }
    async loadRail(rail) {
      const list = handles(rail.dataset.handles);
      const settled = await Promise.allSettled(list.map(getCardData));
      rail.innerHTML = '';
      settled.filter(r => r.status === 'fulfilled').forEach(r => rail.append(card(r.value)));
      if (!rail.children.length) rail.innerHTML = '<p class="tii-prefall__status">Products coming soon.</p>';
    }
    setupCollection() {
      this.collection = this.querySelector('[data-all-products]');
      if (!this.collection) return;
      this.allHandles = handles(this.collection.dataset.allHandles);
      this.cursor = 0;
      this.more = this.querySelector('[data-load-more]');
      this.sentinel = this.querySelector('[data-infinite-sentinel]');
      this.more?.addEventListener('click', () => this.loadMore());
      if (!this.sentinel) return;
      this.collectionObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.loadMore();
      }, { rootMargin: '800px 0px' });
      this.collectionObserver.observe(this.sentinel);
    }
    async loadMore() {
      if (this.loading) return;
      this.loading = true;
      const batch = this.allHandles.slice(this.cursor, this.cursor + 16);
      const settled = await Promise.allSettled(batch.map(getCardData));
      settled.filter(r => r.status === 'fulfilled').forEach(r => this.collection.append(card(r.value)));
      this.cursor += batch.length;
      this.loading = false;
      const complete = this.cursor >= this.allHandles.length;
      if (this.more) this.more.hidden = complete;
      if (complete) {
        this.collectionObserver?.disconnect();
      } else {
        requestAnimationFrame(() => {
          const rect = this.sentinel?.getBoundingClientRect();
          if (rect && rect.top < window.innerHeight + 800) this.loadMore();
        });
      }
    }
  }
  if (!customElements.get('tii-prefall-story')) customElements.define('tii-prefall-story', TiiPrefallStory);
})();
